import { AppData, Registry } from '@lightningjs/sdk';
import { QrCodeInfo } from './LoginRadiusService';

type QrCodeCallbacks = {
  onQrCodeChanged?: (qrCode?: string) => void;
  onLoginCompleted?: () => void;
};

const QR_TOKEN_TIME_TO_LIVE = 600;
const NEW_QR_TOKEN_TIME_TO_FETCH = QR_TOKEN_TIME_TO_LIVE / 2;
const QR_POLLING_INTERVAL = 10;

export default class QrLoginService {
  private currentQrCodes: QrCodeInfo[] = [];
  private pollingTimerId: number | null = null;

  async setupQrCodeTimer(callbacks: QrCodeCallbacks) {
    const isLoggedIn = await this.isQrCodeLoggedIn(callbacks);

    if (!isLoggedIn) {
      this.startQrCodeTimer(callbacks);
    }
  }

  pauseTimer() {
    this.clearTimer();
  }

  reset() {
    this.clearTimer();
    this.currentQrCodes = [];
  }

  private startQrCodeTimer(callbacks: QrCodeCallbacks) {
    this.clearTimer();
    this.pollingTimerId = Registry.setTimeout(async () => {
      this.pollingTimerId = null;
      const isLoggedIn = await this.isQrCodeLoggedIn(callbacks);
      !isLoggedIn && this.startQrCodeTimer(callbacks);
    }, this.getQrPollingRate());
  }

  private clearTimer() {
    if (this.pollingTimerId) {
      Registry.clearTimeout(this.pollingTimerId);
    }
    this.pollingTimerId = null;
  }

  private async isQrCodeLoggedIn(callbacks: QrCodeCallbacks) {
    const result = await this.generateOrPollQrCode();

    if (result?.accessToken) {
      this.reset();
      callbacks.onLoginCompleted?.();
      return true;
    }

    callbacks.onQrCodeChanged?.(result?.code);
    return false;
  }

  private async generateOrPollQrCode() {
    if (this.currentQrCodes.length === 0) {
      await this.generateQrCode();
    } else {
      const qrCode = this.getQrCodeToCheck();
      const now = Date.now();
      const isExpired = qrCode.expiry && qrCode.expiry < now;

      if (!isExpired) {
        await this.pollQrCode(qrCode, now);
      } else {
        this.currentQrCodes = this.currentQrCodes.filter(
          item => item.expiry > now,
        );
        await this.generateQrCode();
      }
    }

    return this.getLatestQrCode();
  }

  private async generateQrCode() {
    const info = await AppData!.loginRadiusService?.generateQrCode(
      QR_TOKEN_TIME_TO_LIVE,
    );

    info && this.currentQrCodes.push(info);
  }

  private getQrCodeToCheck() {
    return this.currentQrCodes.reduce((prev, curr) => {
      if (!prev || !prev.lastCheckedTimestamp) return curr;

      if (prev.lastCheckedTimestamp < curr.lastCheckedTimestamp) {
        return prev;
      }

      return curr;
    }, {} as QrCodeInfo);
  }

  private async pollQrCode(qrCode: QrCodeInfo, now = Date.now()) {
    // Verify polling since it might get asked to poll before it needs to
    const shouldPoll =
      qrCode.lastCheckedTimestamp + this.getQrPollingRate() < now;

    if (shouldPoll) {
      const fetchSecondCode =
        qrCode.created + NEW_QR_TOKEN_TIME_TO_FETCH * 1000 < now &&
        this.currentQrCodes.length <= 1;

      if (fetchSecondCode) {
        await this.generateQrCode();
      } else {
        const token = await AppData!.loginRadiusService
          ?.getAccessTokenFromQrCode(qrCode.code)
          .catch(
            // Delete qr code and recreate for any reason we encounter an error
            async () => {
              this.currentQrCodes = this.currentQrCodes.filter(
                item => item.code !== qrCode.code,
              );
              await this.generateQrCode();
            },
          );

        const qrInfo = this.currentQrCodes.find(
          item => item.code === qrCode.code,
        );
        if (qrInfo) {
          qrInfo.lastCheckedTimestamp = Date.now();
          if (token) qrInfo.accessToken = token;
        }
      }
    }
  }

  private getLatestQrCode() {
    const isLoggedIn = this.currentQrCodes.find(qrCode => qrCode.accessToken);
    const latestQrCode = this.currentQrCodes[this.currentQrCodes.length - 1];

    return isLoggedIn ?? latestQrCode;
  }

  private getQrPollingRate() {
    return (
      (AppData!.ux?.qr_code_poll_interval_seconds || QR_POLLING_INTERVAL) * 1000
    );
  }
}
